import { useState } from 'react';
import { Box, Modal as ModalContainer, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { SMBold, Title, SubTitle } from '@/typography/Typography';
import { InputText } from '@/components/shared/InputText';
import { Dropdown } from '@/components/shared/Dropdown';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { UploadFile } from '@/pages/company/UploadFile';
import dayjs from 'dayjs';
import { useUsersMutations } from '@/services/usersMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';

interface InstaconfigUploadDialogProps {
    open: boolean;
    onClose: () => void;
}

export const InstaconfigUploadDialog = ({
    open,
    onClose
}: InstaconfigUploadDialogProps) => {
    const { t } = useTranslation();
    const { uploadLegalDocument } = useUsersMutations();

    const [consentType, setConsentType] = useState<string>('Data');
    const [documentName, setDocumentName] = useState<string>('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [fileUrl, setFileUrl] = useState<string>('');

    const documentTypeChoices = [
        {
            label: t('license_agreement'),
            value: 'Commercial'
        },
        {
            label: t('data_processing_agreement'),
            value: 'Data'
        }
    ];

    const handleFileUpload = (file: File) => {
        setUploadedFile(file);
        if (!documentName) {
            setDocumentName(file.name);
        }
    };

    const handleClose = () => {
        setConsentType('Data');
        setDocumentName('');
        setUploadedFile(null);
        setFileUrl('');
        onClose();
    };

    useHandleResponse({
        error: uploadLegalDocument.error,
        isSuccess: uploadLegalDocument.isSuccess,
        successAlertMessage: t('document_uploaded_successfully'),
        isSuccessCallBack: handleClose
    });

    const handleActivate = async () => {
        if (!uploadedFile || !consentType) {
            return;
        }

        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('consentType', consentType);
        formData.append('name', documentName);
        formData.append('comment', '');
        // Keep companyId undefined - will be handled globally
        formData.append('latestAcceptanceDate', dayjs().format('YYYY-MM-DD'));

        uploadLegalDocument.mutate(formData);
    };

    const isFormValid = uploadedFile && consentType && documentName;

    return (
        <ModalContainer open={open} onClose={handleClose}>
            <ConfirmationBox style={{ width: '650px' }}>
                <ConnectHeader
                    title={t('upload_global_legal_document')}
                    description={t('global_legal_document_description')}
                />

                <Box alignItems={'flex-start'} mt={2} mb={1}>
                    <Title mb={0.5}>{t('replace_what_document')}</Title>
                    <Dropdown
                        label={''}
                        choices={documentTypeChoices}
                        value={consentType}
                        onChange={(value) => setConsentType(value || 'Data')}
                        allowClear={false}
                    />

                    <Box mt={2}>
                        <Title mb={0.5}>{t('document_name')}</Title>
                        <InputText
                            label={''}
                            placeholder={t('enter_document_name')}
                            value={documentName}
                            onChange={(e) => setDocumentName(e.target.value)}
                        />
                    </Box>

                    <Box mt={2}>
                        <UploadFile
                            handleFileUpload={handleFileUpload}
                            onUploadSuccess={(url) => setFileUrl(url)}
                            description={t('pdf_max_size_description')}
                            accept={{
                                'application/pdf': ['.pdf']
                            }}
                        />
                    </Box>

                    {uploadedFile && (
                        <Typography fontSize={12} mt={1} color="text.secondary">
                            {uploadedFile.name}
                        </Typography>
                    )}
                </Box>

                <Box
                    width={'100%'}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 4,
                        mb: 2
                    }}
                    gap={1.5}
                >
                    <OutlinedSecondaryButtonLG
                        sx={{ width: '50%' }}
                        onClick={handleClose}
                    >
                        <SMBold textTransform={'none'}>{t('cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>

                    <PrimaryButtonLG
                        sx={{ width: '50%' }}
                        onClick={handleActivate}
                        disabled={!isFormValid || uploadLegalDocument.isPending}
                    >
                        <SMBold textTransform={'none'}>
                            {t('upload_and_activate')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
